/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Search } from 'lucide-react';
import { type UseFormReturn, type FieldValues, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FloatingLabelInput } from '@/components/ui/floating-label-input';
import { FormFloatingLabelInput, FormFloatingLabelSelect, FormInput, FormNativeSelect } from '@/components/form-fields';
import { NativeSelectOption } from '@/components/ui/native-select';
import { COUNTRY_CODES } from '@/components/customer-address-form/constants';
import AddressSuggestionDropdown, { type AddressSuggestion } from '@/components/address-suggestion-dropdown';
import { MIN_INPUT_LENGTH, useAutocompleteSuggestions } from '@/hooks/use-autocomplete-suggestions';
import { processAddressSuggestion } from '@/lib/address/address-suggestions';
import { UITarget } from '@/targets/ui-target';
import { stripNonDigits, stripCountryCode } from '@/lib/address/phone-utils';

/**
 * Base address field names that the form must support
 */
export interface AddressFields {
    firstName: string;
    lastName: string;
    address1: string;
    address2: string;
    city: string;
    stateCode: string;
    postalCode: string;
    phoneCountryCode?: string;
    phone?: string;
    addressType?: string;
}

interface GoogleAddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

interface GooglePlaceResult {
    address_components?: GoogleAddressComponent[];
    formatted_address?: string;
    name?: string;
}

interface GoogleMapsListener {
    remove: () => void;
}

interface PlacesAutocompleteInstance {
    addListener: (event: string, handler: () => void) => GoogleMapsListener;
    getPlace: () => GooglePlaceResult;
}

interface PlacesLibrary {
    Autocomplete: new (
        input: HTMLInputElement,
        options?: {
            fields?: string[];
            componentRestrictions?: { country: string | string[] };
        }
    ) => PlacesAutocompleteInstance;
}

/**
 * Props for the AddressFormFields component
 */
export interface AddressFormFieldsProps<TFormValues extends FieldValues> {
    /** React Hook Form instance */
    form: UseFormReturn<TFormValues>;
    /**
     * Prefix for field names (e.g., 'billing' for billing address fields).
     * When provided, field names become 'billingFirstName', 'billingAddress1', etc.
     * When empty, field names are 'firstName', 'address1', etc.
     */
    fieldPrefix?: string;
    /** Whether to show the phone field (default: true) */
    showPhone?: boolean;
    /** Whether to auto-focus a field when the form is shown (default: false) */
    autoFocus?: boolean;
    /** Which field to focus when autoFocus is true: 'firstName' or 'address1' (default: 'address1') */
    autoFocusField?: 'firstName' | 'address1';
    /** Country code for address autocomplete (default: 'US') */
    countryCode?: string;
    /** Custom class name for the container */
    className?: string;
    /**
     * When true, each field shows its label as a placeholder that floats onto the top border on focus /
     * once filled (FloatingLabelInput / FloatingLabelSelect) instead of a label above the box.
     */
    labelsAsPlaceholders?: boolean;
    /** When true, show Country dropdown (e.g. for billing address) */
    showCountry?: boolean;
    /** When true, phone is required (append * to label / placeholder when using placeholder labels) */
    phoneRequired?: boolean;
    /** When true, show the Home/Office Address Type radio selection (default: false) */
    showAddressType?: boolean;
}

/**
 * Shared address form fields component with Google Maps autocomplete integration.
 *
 * This component renders address form fields (firstName, lastName, address1, address2,
 * city, stateCode, postalCode, and optionally phone) with address autocomplete
 * functionality powered by Google Maps Places API.
 *
 * @example
 * ```tsx
 * // For shipping address (no prefix)
 * <AddressFormFields form={form} showPhone={true} autoFocus={isEditing} />
 *
 * // For billing address (with prefix)
 * <AddressFormFields form={form} fieldPrefix="billing" showPhone={false} />
 * ```
 */
export function AddressFormFields<TFormValues extends FieldValues>({
    form,
    fieldPrefix = '',
    showPhone = true,
    autoFocus = false,
    autoFocusField = 'address1',
    countryCode = 'IN',
    className,
    labelsAsPlaceholders = false,
    showCountry = false,
    phoneRequired: _phoneRequired = false,
    showAddressType = false,
}: AddressFormFieldsProps<TFormValues>) {
    const { t } = useTranslation('checkout');
    const { t: tCountries } = useTranslation('countries');

    // Address 2 autocomplete state
    const [address2Input, setAddress2Input] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);

    /**
     * Helper to construct field names with optional prefix
     * e.g., with prefix 'billing': 'firstName' becomes 'billingFirstName'
     */
    const getFieldName = useCallback(
        (baseName: string): Path<TFormValues> => {
            if (!fieldPrefix) {
                return baseName as Path<TFormValues>;
            }
            // Capitalize first letter of baseName when prefixing
            const capitalizedBaseName = baseName.charAt(0).toUpperCase() + baseName.slice(1);
            return `${fieldPrefix}${capitalizedBaseName}` as Path<TFormValues>;
        },
        [fieldPrefix]
    );

    /**
     * Helper to construct autoComplete attribute with proper section prefix
     * e.g., with prefix 'billing': 'given-name' becomes 'billing given-name'
     * e.g., with prefix '' (shipping): 'given-name' becomes 'shipping given-name'
     */
    const getAutoComplete = (autoCompleteValue: string): string => {
        const section = fieldPrefix || 'shipping';
        return `${section} ${autoCompleteValue}`;
    };

    // When showCountry is true (e.g. billing), watch country so we can show State/Province and Zip Code/Postal Code labels
    const watchedCountry = showCountry ? form.watch(getFieldName('countryCode')) : undefined;
    const effectiveCountry = showCountry ? watchedCountry || countryCode || 'IN' : countryCode || 'IN';
    const useProvinceLabel = effectiveCountry === 'CA';
    const useZipLabel = effectiveCountry === 'US';

    // Use the autocomplete suggestions hook for Google Maps Places API on address2 ONLY
    // address1 does NOT use autocomplete
    const {
        suggestions: addressSuggestions,
        isLoading: isLoadingSuggestions,
        resetSession,
    } = useAutocompleteSuggestions({
        inputString: address2Input,
        countryCode: effectiveCountry || 'IN',
    });

    const statesObj = useMemo(() => {
        return effectiveCountry && (effectiveCountry === 'US' || effectiveCountry === 'CA' || effectiveCountry === 'IN')
            ? (tCountries(`${effectiveCountry}.states`, { returnObjects: true }) as Record<string, string>)
            : null;
    }, [effectiveCountry, tCountries]);

    const placesLibrary = useMapsLibrary('places');
    const address2InputRef = useRef<HTMLInputElement | null>(null);

    // Resolve state code from full name or abbreviation to match storefront state select options
    const resolveStateCode = useCallback(
        (shortName: string, longName: string): string => {
            if (!statesObj) return shortName || longName;
            const cleanShort = (shortName || '').trim().toUpperCase();
            if (statesObj[cleanShort]) return cleanShort;

            const lowerLong = (longName || '').trim().toLowerCase();
            const lowerShort = (shortName || '').trim().toLowerCase();

            for (const [code, name] of Object.entries(statesObj)) {
                if (
                    code.toLowerCase() === lowerShort ||
                    name.toLowerCase() === lowerLong ||
                    name.toLowerCase() === lowerShort
                ) {
                    return code;
                }
            }
            return shortName || longName;
        },
        [statesObj]
    );

    // Initialize Google Places Autocomplete on the Address Line 2 input box
    useEffect(() => {
        let isCancelled = false;
        let listener: GoogleMapsListener | null = null;
        let keydownHandler: ((e: KeyboardEvent) => void) | null = null;

        const initPlacesAutocomplete = (): boolean => {
            if (isCancelled) return false;
            const input = address2InputRef.current;
            const windowPlaces =
                typeof window !== 'undefined'
                    ? (window as unknown as { google?: { maps?: { places?: PlacesLibrary } } }).google?.maps?.places
                    : null;
            const placesLib = placesLibrary || windowPlaces;
            if (!placesLib?.Autocomplete || !input) return false;

            const country = (effectiveCountry || 'IN').toLowerCase();
            const options: {
                fields: string[];
                componentRestrictions?: { country: string | string[] };
            } = {
                fields: ['address_components', 'formatted_address', 'geometry', 'name'],
            };
            if (country) {
                options.componentRestrictions = { country };
            }

            const autocompleteInstance = new placesLib.Autocomplete(input, options);

            // Prevent checkout form from submitting if user hits Enter to select a suggestion
            keydownHandler = (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    const pacContainer = document.querySelector('.pac-container');
                    if (pacContainer && window.getComputedStyle(pacContainer).display !== 'none') {
                        e.preventDefault();
                    }
                }
            };
            input.addEventListener('keydown', keydownHandler);

            listener = autocompleteInstance.addListener('place_changed', () => {
                setShowSuggestions(false);
                const place = autocompleteInstance.getPlace();
                if (!place || !place.address_components) return;

                const getComp = (type: string): string =>
                    place.address_components?.find((c: GoogleAddressComponent) => c.types.includes(type))?.long_name ||
                    '';
                const getCompShort = (type: string): string =>
                    place.address_components?.find((c: GoogleAddressComponent) => c.types.includes(type))?.short_name ||
                    '';

                const subpremise = getComp('subpremise');
                const premise = getComp('premise');
                const streetNumber = getComp('street_number');
                const route = getComp('route');
                const sublocality2 = getComp('sublocality_level_2');
                const sublocality1 = getComp('sublocality_level_1') || getComp('sublocality');
                const neighborhood = getComp('neighborhood');

                const localityParts = [
                    subpremise,
                    premise,
                    [streetNumber, route].filter(Boolean).join(' '),
                    sublocality2,
                    sublocality1,
                    neighborhood,
                ].filter(Boolean);

                const address2Value =
                    localityParts.length > 0
                        ? Array.from(new Set(localityParts)).join(', ')
                        : place.name || input.value;

                const cityValue =
                    getComp('locality') ||
                    getComp('postal_town') ||
                    getComp('administrative_area_level_3') ||
                    sublocality1;

                const rawStateShort = getCompShort('administrative_area_level_1');
                const rawStateLong = getComp('administrative_area_level_1');
                const stateValue = resolveStateCode(rawStateShort, rawStateLong);
                const postalCodeValue = getComp('postal_code');

                if (address2Value) {
                    form.setValue(getFieldName('address2'), address2Value as TFormValues[Path<TFormValues>], {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                    });
                }
                if (cityValue) {
                    form.setValue(getFieldName('city'), cityValue as TFormValues[Path<TFormValues>], {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                    });
                }
                if (stateValue) {
                    form.setValue(getFieldName('stateCode'), stateValue as TFormValues[Path<TFormValues>], {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                    });
                }
                if (postalCodeValue) {
                    form.setValue(getFieldName('postalCode'), postalCodeValue as TFormValues[Path<TFormValues>], {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                    });
                }
            });

            return true;
        };

        if (!initPlacesAutocomplete()) {
            const interval = setInterval(() => {
                if (initPlacesAutocomplete()) {
                    clearInterval(interval);
                }
            }, 300);

            return () => {
                isCancelled = true;
                clearInterval(interval);
                if (listener?.remove) {
                    listener.remove();
                } else if (typeof window !== 'undefined') {
                    (
                        window as unknown as {
                            google?: { maps?: { event?: { removeListener: (l: unknown) => void } } };
                        }
                    ).google?.maps?.event?.removeListener?.(listener);
                }
                if (keydownHandler && address2InputRef.current) {
                    address2InputRef.current.removeEventListener('keydown', keydownHandler);
                }
            };
        }

        return () => {
            isCancelled = true;
            if (listener?.remove) {
                listener.remove();
            } else if (typeof window !== 'undefined') {
                (
                    window as unknown as {
                        google?: { maps?: { event?: { removeListener: (l: unknown) => void } } };
                    }
                ).google?.maps?.event?.removeListener?.(listener);
            }
            if (keydownHandler && address2InputRef.current) {
                address2InputRef.current.removeEventListener('keydown', keydownHandler);
            }
        };
    }, [effectiveCountry, form, getFieldName, placesLibrary, resolveStateCode]);

    const handleSelectSuggestion = async (suggestion: AddressSuggestion) => {
        setShowSuggestions(false);

        // Process the suggestion to get structured address fields
        const addressFields = await processAddressSuggestion(suggestion);

        const displayText =
            suggestion.structured_formatting?.main_text ||
            suggestion.description?.split(',')[0] ||
            suggestion.description ||
            addressFields.address1 ||
            '';

        // Populate address2 and location fields, leaving address1 untouched
        form.setValue(getFieldName('address2'), displayText as TFormValues[Path<TFormValues>], {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });

        if (addressFields.city) {
            form.setValue(getFieldName('city'), addressFields.city as TFormValues[Path<TFormValues>], {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            });
        }
        if (addressFields.stateCode) {
            const resolvedState = resolveStateCode(addressFields.stateCode, addressFields.stateCode);
            form.setValue(getFieldName('stateCode'), resolvedState as TFormValues[Path<TFormValues>], {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            });
        }
        if (addressFields.postalCode) {
            form.setValue(getFieldName('postalCode'), addressFields.postalCode as TFormValues[Path<TFormValues>], {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            });
        }

        // Reset the autocomplete session after selection
        resetSession();
        setAddress2Input('');
    };

    const handleCloseSuggestions = () => {
        setShowSuggestions(false);
    };

    const handleAddress2InputChange = (e: ChangeEvent<HTMLInputElement>, fieldOnChange: (value: string) => void) => {
        const value = e.target.value;
        fieldOnChange(value);
        setAddress2Input(value);
        // Show suggestions dropdown when user starts typing in address2
        if (value.length >= MIN_INPUT_LENGTH) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    /**
     * Renders the address autocomplete dropdown with target extensibility under address2.
     */
    const renderAddress2Autocomplete = (): React.ReactNode => {
        if (!showSuggestions || addressSuggestions.length === 0) {
            return null;
        }

        const dropdown = (
            <AddressSuggestionDropdown
                suggestions={addressSuggestions}
                isVisible={showSuggestions}
                isLoading={isLoadingSuggestions}
                onClose={handleCloseSuggestions}
                onSelectSuggestion={(suggestion) => void handleSelectSuggestion(suggestion)}
            />
        );

        if (fieldPrefix === 'billing') {
            return (
                <div>
                    <UITarget targetId="sfcc.checkout.payment.billingAddress.autocomplete">{dropdown}</UITarget>
                </div>
            );
        }

        return (
            <div>
                <UITarget targetId="sfcc.checkout.shippingAddress.autocomplete">{dropdown}</UITarget>
            </div>
        );
    };



    return (
        <div className={className}>
            {/* Row 1: First Name and Last Name */}
            <div className="grid md:grid-cols-2 gap-4 mb-4 items-start">
                <FormField
                    control={form.control}
                    name={getFieldName('firstName')}
                    render={({ field }) => (
                        <FormItem>
                            {labelsAsPlaceholders ? (
                                <FormFloatingLabelInput
                                    label={`${t('addressForm.firstNameLabel')}*`}
                                    autoComplete={getAutoComplete('given-name')}
                                    autoFocus={autoFocus && autoFocusField === 'firstName'}
                                    {...field}
                                />
                            ) : (
                                <>
                                    <FormLabel>{t('addressForm.firstNameLabel')}*</FormLabel>
                                    <FormInput
                                        placeholder={t('addressForm.firstNamePlaceholder')}
                                        autoComplete={getAutoComplete('given-name')}
                                        autoFocus={autoFocus && autoFocusField === 'firstName'}
                                        {...field}
                                    />
                                </>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name={getFieldName('lastName')}
                    render={({ field }) => (
                        <FormItem>
                            {labelsAsPlaceholders ? (
                                <FormFloatingLabelInput
                                    label={`${t('addressForm.lastNameLabel')}*`}
                                    autoComplete={getAutoComplete('family-name')}
                                    {...field}
                                />
                            ) : (
                                <>
                                    <FormLabel>{t('addressForm.lastNameLabel')}*</FormLabel>
                                    <FormInput
                                        placeholder={t('addressForm.lastNamePlaceholder')}
                                        autoComplete={getAutoComplete('family-name')}
                                        {...field}
                                    />
                                </>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            {/* Row 2: Contact Number (Phone Field) */}
            {showPhone && (
                <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                    <div className="col-span-2 sm:col-span-1 flex items-start gap-2">
                        {/* Phone country code commented out
                        <FormField
                            control={form.control}
                            name={getFieldName('phoneCountryCode')}
                            render={({ field }) => (
                                <FormItem className="w-20 shrink-0">
                                    <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                        {t('addressForm.codeLabel')}
                                    </FormLabel>
                                    <FormNativeSelect
                                        aria-label={t('addressForm.codeLabel')}
                                        value={field.value || '+91'}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}>
                                        {countryCodeOptions}
                                    </FormNativeSelect>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /> */}
                        <FormField
                            control={form.control}
                            name={getFieldName('phone')}
                            render={({ field }) => (
                                <FormItem className="flex-1">
                                    {labelsAsPlaceholders ? (
                                        <FormFloatingLabelInput
                                            label="Contact Number *"
                                            aria-label={`${t('addressForm.phoneLabel')}*`}
                                            prefix="+91"
                                            type="tel"
                                            inputMode="numeric"
                                            autoComplete={getAutoComplete('tel-national')}
                                            maxLength={14}
                                            {...field}
                                            value={stripCountryCode(field.value || '')}
                                            onChange={(e) => {
                                                field.onChange(stripNonDigits(e.target.value).slice(0, 10));
                                            }}
                                            onBlur={() => {
                                                field.onBlur();
                                            }}
                                        />
                                    ) : (
                                        <>
                                            <FormLabel>{t('addressForm.phoneLabel')}*</FormLabel>
                                            <FormInput
                                                type="tel"
                                                inputMode="numeric"
                                                placeholder={t('addressForm.phonePlaceholder')}
                                                autoComplete={getAutoComplete('tel-national')}
                                                maxLength={14}
                                                {...field}
                                                value={stripCountryCode(field.value || '')}
                                                onChange={(e) => {
                                                    field.onChange(stripNonDigits(e.target.value).slice(0, 10));
                                                }}
                                                onBlur={() => {
                                                    field.onBlur();
                                                }}
                                            />
                                        </>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
            )}

            {/* Row 3: Address Line 1 */}
            <div className="mb-4">
                <FormField
                    control={form.control}
                    name={getFieldName('address1')}
                    render={({ field }) => (
                        <FormItem>
                            {labelsAsPlaceholders ? (
                                <FormFloatingLabelInput
                                    label="Flat No., Block No., Apartment *"
                                    aria-label={`${t('addressForm.addressLabel')}*`}
                                    autoComplete={getAutoComplete('address-line1')}
                                    autoFocus={autoFocus && autoFocusField === 'address1'}
                                    {...field}
                                />
                            ) : (
                                <>
                                    <FormLabel>{t('addressForm.addressLabel')}*</FormLabel>
                                    <FormInput
                                        placeholder={t('addressForm.addressPlaceholder')}
                                        autoComplete={getAutoComplete('address-line1')}
                                        autoFocus={autoFocus && autoFocusField === 'address1'}
                                        {...field}
                                    />
                                </>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            {/* Row 4: Address Line 2 with Google Places Autocomplete */}
            <div className="mb-4">
                <FormField
                    control={form.control}
                    name={getFieldName('address2')}
                    render={({ field }) => (
                        <FormItem className="relative">
                            {labelsAsPlaceholders ? (
                                <FormFloatingLabelInput
                                    label="Address"
                                    aria-label={t('addressForm.address2Label')}
                                    autoComplete="off"
                                    suffix={<Search aria-hidden="true" className="size-4 text-muted-foreground" />}
                                    {...field}
                                    ref={(e) => {
                                        field.ref(e);
                                        address2InputRef.current = e;
                                    }}
                                    onChange={(e) => handleAddress2InputChange(e, field.onChange)}
                                />
                            ) : (
                                <>
                                    <FormLabel>{t('addressForm.address2Label')}</FormLabel>
                                    <FormInput
                                        placeholder={t('addressForm.address2Placeholder')}
                                        autoComplete="off"
                                        {...field}
                                        ref={(e) => {
                                            field.ref(e);
                                            address2InputRef.current = e;
                                        }}
                                        onChange={(e) => handleAddress2InputChange(e, field.onChange)}
                                    />
                                </>
                            )}
                            {renderAddress2Autocomplete()}
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            {/* Row 5: City and State / Province Row */}
            <div className="grid md:grid-cols-2 gap-4 mb-4 items-start">
                <FormField
                    control={form.control}
                    name={getFieldName('city')}
                    render={({ field }) => (
                        <FormItem>
                            {labelsAsPlaceholders ? (
                                <FormFloatingLabelInput
                                    label={`${t('addressForm.cityLabel')}*`}
                                    autoComplete={getAutoComplete('address-level2')}
                                    {...field}
                                />
                            ) : (
                                <>
                                    <FormLabel>{t('addressForm.cityLabel')}*</FormLabel>
                                    <FormInput
                                        placeholder={t('addressForm.cityPlaceholder')}
                                        autoComplete={getAutoComplete('address-level2')}
                                        {...field}
                                    />
                                </>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name={getFieldName('stateCode')}
                    render={({ field }) => {
                        const stateOptions = statesObj ? Object.entries(statesObj) : [];
                        const stateLabel = useProvinceLabel
                            ? t('addressForm.provinceLabel')
                            : t('addressForm.stateLabel');
                        const statePlaceholder = useProvinceLabel
                            ? t('addressForm.provincePlaceholder')
                            : t('addressForm.statePlaceholder');

                        return (
                            <FormItem className="[&_[data-slot=native-select-wrapper]]:w-full">
                                {labelsAsPlaceholders ? (
                                    stateOptions.length > 0 ? (
                                        <FormFloatingLabelSelect
                                            label={`${stateLabel}*`}
                                            autoComplete={getAutoComplete('address-level1')}
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                field.onChange(e.target.value)
                                            }>
                                            {stateOptions.map(([code, name]) => (
                                                <NativeSelectOption key={code} value={code}>
                                                    {name}
                                                </NativeSelectOption>
                                            ))}
                                        </FormFloatingLabelSelect>
                                    ) : (
                                        <FormFloatingLabelInput
                                            label={`${stateLabel}*`}
                                            autoComplete={getAutoComplete('address-level1')}
                                            {...field}
                                        />
                                    )
                                ) : (
                                    <>
                                        <FormLabel>{stateLabel}*</FormLabel>
                                        {stateOptions.length > 0 ? (
                                            <FormNativeSelect
                                                autoComplete={getAutoComplete('address-level1')}
                                                className={!field.value ? 'text-muted-foreground' : ''}
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                    field.onChange(e.target.value)
                                                }>
                                                <NativeSelectOption value="" className="text-muted-foreground">
                                                    {statePlaceholder}
                                                </NativeSelectOption>
                                                {stateOptions.map(([code, name]) => (
                                                    <NativeSelectOption key={code} value={code}>
                                                        {name}
                                                    </NativeSelectOption>
                                                ))}
                                            </FormNativeSelect>
                                        ) : (
                                            <FormInput
                                                placeholder={statePlaceholder}
                                                autoComplete={getAutoComplete('address-level1')}
                                                {...field}
                                            />
                                        )}
                                    </>
                                )}
                                <FormMessage />
                            </FormItem>
                        );
                    }}
                />
            </div>

            {/* Row 6: Zip Code / Postal Code and Country Row */}
            <div className="grid md:grid-cols-2 gap-4 mb-4 items-start">
                <FormField
                    control={form.control}
                    name={getFieldName('postalCode')}
                    render={({ field }) => {
                        const postalLabel = useZipLabel ? t('addressForm.zipLabel') : t('addressForm.postalCodeLabel');
                        const postalPlaceholder = useZipLabel
                            ? t('addressForm.zipPlaceholder')
                            : t('addressForm.postalCodePlaceholder');
                        return (
                            <FormItem>
                                {labelsAsPlaceholders ? (
                                    <FormFloatingLabelInput
                                        label={`${postalLabel}*`}
                                        autoComplete={getAutoComplete('postal-code')}
                                        {...field}
                                    />
                                ) : (
                                    <>
                                        <FormLabel>{postalLabel}*</FormLabel>
                                        <FormInput
                                            placeholder={postalPlaceholder}
                                            autoComplete={getAutoComplete('postal-code')}
                                            {...field}
                                        />
                                    </>
                                )}
                                <FormMessage />
                            </FormItem>
                        );
                    }}
                />

                {showCountry ? (
                    <FormField
                        control={form.control}
                        name={getFieldName('countryCode')}
                        render={({ field }) => (
                            <FormItem className="[&_[data-slot=native-select-wrapper]]:w-full">
                                {labelsAsPlaceholders ? (
                                    <FormFloatingLabelSelect
                                        label={`${t('addressForm.countryLabel')}*`}
                                        autoComplete={getAutoComplete('country')}
                                        {...field}
                                        value={field.value || 'IN'}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                            field.onChange(e.target.value || 'IN')
                                        }>
                                        {COUNTRY_CODES.map((code) => (
                                            <NativeSelectOption key={code} value={code}>
                                                {tCountries(`${code}.name`)}
                                            </NativeSelectOption>
                                        ))}
                                    </FormFloatingLabelSelect>
                                ) : (
                                    <>
                                        <FormLabel>{t('addressForm.countryLabel')}*</FormLabel>
                                        <FormNativeSelect
                                            autoComplete={getAutoComplete('country')}
                                            aria-label={t('addressForm.countryLabel')}
                                            {...field}
                                            value={field.value || 'IN'}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                field.onChange(e.target.value || 'IN')
                                            }>
                                            {COUNTRY_CODES.map((code) => (
                                                <NativeSelectOption key={code} value={code}>
                                                    {tCountries(`${code}.name`)}
                                                </NativeSelectOption>
                                            ))}
                                        </FormNativeSelect>
                                    </>
                                )}
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                ) : labelsAsPlaceholders ? (
                    <FormItem>
                        <FloatingLabelInput
                            label={`${t('addressForm.countryLabel')}*`}
                            value={tCountries(`${countryCode}.name` as `US.name`)}
                            readOnly
                            tabIndex={-1}
                        />
                    </FormItem>
                ) : (
                    <FormItem>
                        <FormLabel>{t('addressForm.countryLabel')}*</FormLabel>
                        <Input
                            value={tCountries(`${countryCode}.name` as `US.name`)}
                            readOnly
                            aria-label={t('addressForm.countryLabel')}
                            className="text-foreground bg-background cursor-default"
                            tabIndex={-1}
                        />
                    </FormItem>
                )}
            </div>

            {showAddressType && (
                <FormField
                    control={form.control}
                    name={getFieldName('addressType')}
                    render={({ field }) => (
                        <FormItem className="space-y-0">
                            <div className="address-type-row flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                                <span className="address-type-label text-sm font-medium text-foreground">
                                    {t('addressForm.addressTypeLabel', { defaultValue: 'Address Type' })}*
                                </span>
                                <div className="flex items-center gap-6">
                                    <label
                                        htmlFor={`${getFieldName('addressType')}-home`}
                                        className="flex h-[22px] w-auto items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="radio"
                                            id={`${getFieldName('addressType')}-home`}
                                            name={getFieldName('addressType')}
                                            value="home"
                                            checked={field.value === 'home'}
                                            onChange={() => field.onChange('home')}
                                            className="w-4 h-4 shrink-0 cursor-pointer checked:appearance-none checked:rounded-full checked:border-[5px] checked:border-solid checked:border-foreground checked:bg-background"
                                        />
                                        {t('addressForm.addressTypeHome', { defaultValue: 'Home' })}
                                    </label>
                                    <label
                                        htmlFor={`${getFieldName('addressType')}-office`}
                                        className="flex h-[22px] w-auto items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="radio"
                                            id={`${getFieldName('addressType')}-office`}
                                            name={getFieldName('addressType')}
                                            value="office"
                                            checked={field.value === 'office'}
                                            onChange={() => field.onChange('office')}
                                            className="w-4 h-4 shrink-0 cursor-pointer checked:appearance-none checked:rounded-full checked:border-[5px] checked:border-solid checked:border-foreground checked:bg-background"
                                        />
                                        {t('addressForm.addressTypeOffice', { defaultValue: 'Office' })}
                                    </label>
                                </div>
                            </div>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            )}
        </div>
    );
}

export default AddressFormFields;
